<script lang="ts">
</script>

<script setup lang="ts">
import VueCountdown from '@chenfengyuan/vue-countdown'
import { onClickOutside } from '@vueuse/core'
import { defineAsyncComponent } from 'vue'
import SeperatorImg from '@/assets/components/categoryOffersBlock/separator.webp?url'
import clockIconUrl from '@/assets/components/countdownTIle/clock.webp'
import bgPurple from '@/assets/components/gameCategory/bg_pattern_purple.png?url'

export type ComputedMilestone = Games.LoyaltySeasonMilestone & {
  claimed: boolean
  isNextNotTaken: boolean
  progressPct: number
  asset: string
  humanAmount: string
  pointsRequired: string
}

const props = withDefaults(defineProps<Props>(), {
  loyaltySeason: () => ({}),
  loyaltySeasonMilestones: () => ({}),
  seasonEndsAtMs: () => ({}),
  stickyMinCount: 8,
  disableStickyVariant: false
})
const LoyaltyGameMilestonesBoxComponent = defineAsyncComponent(() => import('./LoyaltyGameMilestonesBoxComponent.vue'))
const LoyaltyGameMilestonesBoxComponentSticky = defineAsyncComponent(() => import('./LoyaltyGameMilestonesBoxComponentSticky.vue'))

interface Props {
  loyaltySeason?: { [key in Games.GameKeyType]?: Games.LoyaltySeason | null }
  loyaltySeasonMilestones?: { [key in Games.GameKeyType]?: Games.LoyaltySeasonMilestone[] }
  seasonEndsAtMs?: { [key in Games.GameKeyType]?: number | null }
  stickyMinCount?: number
  disableStickyVariant?: boolean
}

const { routeGame, routeGameKey, numbersShortHuman, isLogged } = gamesUtils
const { t } = useI18n()

const tooltipEl = ref<HTMLElement | null>(null)
const showTooltip = ref(false)
onClickOutside(tooltipEl, () => {
  showTooltip.value = false
})

const timerMs = computed(() => props.seasonEndsAtMs?.[routeGameKey.value || ''] ?? null)

const milestones = computed((): ComputedMilestone[] => {
  const list = props.loyaltySeasonMilestones?.[routeGameKey.value || ''] ?? []
  const playerPoints = props.loyaltySeason?.[routeGameKey.value || '']?.playerPoints ?? 0
  let nextFound = false
  return list.map((m, index) => {
    const claimed = m.taken === 1
    const isNextNotTaken = !claimed && !nextFound
    if (isNextNotTaken)
      nextFound = true
    const prevReqValue = list[index - 1]?.req_value ?? 0
    const range = m.req_value - prevReqValue
    const progressPct = claimed
      ? 100
      : range > 0 ? Math.min(100, Math.max(0, ((playerPoints - prevReqValue) / range) * 100)) : 0
    const item = m.items?.[0]
    const asset = item?.asset
      ? (item.asset.startsWith('http') ? item.asset : `${routeGame.value?.imgStorageUrl || ''}${item.asset}`)
      : ''
    const humanAmount = item?.amount ? numbersShortHuman(item.amount) : ''
    const pointsRequired = numbersShortHuman(m.req_value - (list[index - 1]?.req_value ?? 0))
    return { ...m, claimed, isNextNotTaken, progressPct, asset, humanAmount, pointsRequired }
  })
})

const claimedCount = computed(() => milestones.value.filter(m => m.claimed).length)

const useStickyVariant = computed(() =>
  !props.disableStickyVariant && milestones.value.length >= props.stickyMinCount
)

const isMounted = ref(false)
const isSliderOpen = ref(false)

onMounted(() => {
  isMounted.value = true
})

watch([isMounted, milestones], ([mounted, list]) => {
  if (mounted && list.length && !isSliderOpen.value) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isSliderOpen.value = true
      })
    })
  }
})
</script>

<template>
  <div
    v-if="milestones.length"
    class="relative w-full pb-1"
  >
    <!-- top separator -->
    <img
      alt=""
      class="w-full absolute left-0 right-0 -translate-y-1/2 z-10"
      :src="SeperatorImg"
    >

    <!-- section -->
    <section class="relative overflow-hidden ms-section">
      <div
        class="absolute inset-0 bg-cover bg-center bg-no-repeat"
        :style="`background-image: url('${bgPurple}')`"
      />
      <div class="absolute inset-0 pointer-events-none ms-vignette" />

      <div class="relative sm:max-w-xl big:max-w-5xl mx-auto px-3 big:px-6 big:pt-6 ms-outer">
        <!-- top row -->
        <div class="flex items-center justify-between pb-3.5">
          <div>
            <div
              v-if="timerMs"
              class="relative inline-flex items-center justify-center text-white bg-[#ffb000] rounded-lg pl-5 pr-2 h-[26px] text-sm font-black shadow-xs"
            >
              <el-image
                :src="clockIconUrl"
                lazy
                class="w-8 absolute -left-3 -top-2.5"
              />
              <VueCountdown :time="timerMs">
                <template #default="{ days, hours, minutes }">
                  <template v-if="days">
                    {{ days }}d {{ hours }}h
                  </template>
                  <template v-else-if="hours">
                    {{ hours }}h {{ minutes }}m
                  </template>
                  <template v-else>
                    {{ minutes }}m
                  </template>
                </template>
              </VueCountdown>
            </div>
          </div>
          <div
            ref="tooltipEl"
            class="relative"
          >
            <button
              class="text-[#4fd1e5] font-bold text-[12px] tracking-[0.04em] uppercase transition-colors hover:text-white cursor-pointer"
              @click="showTooltip = !showTooltip"
            >
              {{ t('milestones.rules') }} ›
            </button>
            <Transition name="el-fade-in-linear">
              <div
                v-if="showTooltip"
                class="ms-tooltip absolute right-0 top-full mt-2 w-[290px] rounded-[6px] p-3 z-50 shadow-xl"
              >
                <div class="text-[#ffd23a] font-extrabold text-[11px] uppercase tracking-[0.04em] pb-2 mb-2 border-b border-[#3a2c08]">
                  {{ t('milestones.rulesTitle') }}
                </div>
                <p class="text-[#cfd9dd] font-semibold italic text-[11px] leading-relaxed m-0">
                  {{ t('milestones.rulesBody') }}
                </p>
              </div>
            </Transition>
          </div>
        </div>

        <!-- section title -->
        <div class="mb-0.5 tracking-wider leading-[1.5em]">
          <div class="text-center text-white uppercase text-[0.85rem] mini:text-[1rem] font-black tracking-wider leading-[1.5em]">
            {{ t('milestones.title') }}
          </div>
          <div class="text-center text-white uppercase text-[0.65rem] mini:text[0.9rem] font-light">
            {{ t('milestones.subtitle') }}
          </div>
          <div class="text-white italic normal-case text-[0.8rem] mini:text-[0.9rem] font-black tracking-wider leading-[1.5em] mt-4">
            {{ t('milestones.step', { current: claimedCount, total: milestones.length }) }}
          </div>
        </div>

        <!-- slider (client-only: Flicking accesses document at module level) -->
        <div
          class="ms-slider-wrap"
          :class="{ 'ms-slider-wrap--open': isSliderOpen, 'ms-slider-wrap--tight-right': useStickyVariant }"
        >
          <div
            class="ms-slider-inner"
            :class="{ 'ms-slider-inner--wide': !useStickyVariant }"
          >
            <template v-if="isMounted">
              <LoyaltyGameMilestonesBoxComponentSticky
                v-if="useStickyVariant"
                :milestones="milestones"
                :is-logged="isLogged"
              />
              <LoyaltyGameMilestonesBoxComponent
                v-else
                :milestones="milestones"
                :is-logged="isLogged"
              />
            </template>
          </div>
        </div>
      </div>
    </section>

    <!-- bottom separator -->
    <img
      alt=""
      class="w-full absolute left-0 right-0 bottom-0 translate-y-1/2 z-10"
      :src="SeperatorImg"
    >
  </div>
</template>

<style scoped lang="postcss">
.ms-outer {
  container-type: inline-size;
}

.ms-section {
  padding: 30px 0 32px;
  background: #0a0420;
}

.ms-slider-wrap {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.6s ease;

  @media (width > 820px) {
    margin-left: -48px;
  }

  @media (width <= 820px) {
    margin-left: -20px;
  }

  &.ms-slider-wrap--open {
    grid-template-rows: 1fr;
  }

  /* sticky only: pulls the sticky tile a bit closer to the right edge.
     Scoped here (not applied unconditionally) so it doesn't also widen
     .ms-slider-inner--wide's "100%" reference for the non-sticky variant */
  &.ms-slider-wrap--tight-right {
    @media (width <= 820px) {
      margin-right: -5px;
    }
  }
}

.ms-slider-inner {
  overflow: hidden;
  min-height: 0;

  @media (width <= 820px) {
    /* the tile's hover/active scale transform gets clipped at the very top by this
       element's own overflow:hidden without a little breathing room above it */
    padding-top: 3px;
  }
}

.ms-slider-inner--wide {
  /* non-sticky only: the sticky variant's own right edge is the sticky tile, which
     must stay flush with the container's natural gutter. Non-sticky has no such tile,
     so its last edge can bleed right by the same amount slider-wrap already bleeds left,
     keeping the right-edge fade symmetric with the left one */
  @media (width <= 820px) {
    width: calc(100% + 20px);
  }
}

.ms-vignette {
  background: radial-gradient(120% 90% at 50% 50%, transparent 60%, rgb(0 0 0 / 18%) 100%);
}

.ms-tooltip {
  background: #0a1a20;
  border: 1px solid #ffd23a;
}
</style>
